import { useTheme } from 'context/ThemeContext'
import { Feature, FeatureCollection } from 'geojson'
import { LngLatBoundsLike } from 'mapbox-gl'
import { useEffect } from 'react'
import { Layer, Source, useMap } from 'react-map-gl'
import { IRobotMap } from 'utils/interfaces'

interface RobotMapOnMapProps {
  selectedMap?: IRobotMap
  panelOffset?: number
  erasedPolygons?: Feature[]
}

const RobotMapOnMap = ({ selectedMap, panelOffset = 580, erasedPolygons = [] }: RobotMapOnMapProps) => {
  const { current: map } = useMap()
  const { theme } = useTheme()

  const erasedGeoJson: FeatureCollection = {
    type: 'FeatureCollection',
    features: erasedPolygons,
  }

  const bboxString = JSON.stringify(selectedMap?.bbox || null)

  useEffect(() => {
    if (selectedMap?.bbox && map) {
      const coords = (selectedMap.bbox as any).coordinates?.[0]
      if (coords && coords.length > 0) {
        const bounds = coords.reduce(
          (acc: [number, number, number, number], coord: [number, number]) => [
            Math.min(acc[0], coord[0]),
            Math.min(acc[1], coord[1]),
            Math.max(acc[2], coord[0]),
            Math.max(acc[3], coord[1]),
          ],
          [Infinity, Infinity, -Infinity, -Infinity]
        )
        if (bounds[0] !== Infinity) {
          const container = map.getContainer()
          const mapWidth = container.clientWidth
          const leftBuffer = Math.min(panelOffset + 40, mapWidth * 0.45)

          map.fitBounds(bounds as LngLatBoundsLike, {
            padding: { top: 140, bottom: 40, right: 40, left: leftBuffer },
            duration: 1000,
          })
        }
      }
    }
  }, [selectedMap?.id, bboxString, map, panelOffset])

  if (!selectedMap || !selectedMap.file_tiles) {
    return null
  }

  // file_tiles from DB is a relative path like 'map/marina_parking/map.tiles'
  // or sometimes a full URL with internal Docker host.
  // We need to build a browser-accessible URL with the {z}/{x}/{y} template.
  let tilesBaseUrl = selectedMap.file_tiles
  let queryParams = ''

  if (tilesBaseUrl.includes('?')) {
    const parts = tilesBaseUrl.split('?')
    tilesBaseUrl = parts[0]
    queryParams = '?' + parts[1]
  }

  if (tilesBaseUrl.startsWith('http')) {
    // Full URL from backend — fix internal Docker hostname
    if (tilesBaseUrl.includes('//nginx/')) {
      tilesBaseUrl = tilesBaseUrl.replace('//nginx/', `//${window.location.host}/`)
    }
    // Ensure protocol matches the current page to avoid SSL errors in dev
    const currentProtocol = window.location.protocol
    tilesBaseUrl = tilesBaseUrl.replace(/^https?:/, currentProtocol)
  } else {
    // Relative path — prepend current origin + /storage/
    const prefix = tilesBaseUrl.startsWith('/') ? '' : '/storage/'
    tilesBaseUrl = `${window.location.origin}${prefix}${tilesBaseUrl}`
  }

  // Ensure trailing slash before the template
  if (!tilesBaseUrl.endsWith('/')) {
    tilesBaseUrl += '/'
  }

  // Mapbox expects a URL template for raster tiles
  const tilesUrl = `${tilesBaseUrl}{z}/{x}/{y}.png${queryParams}`

  return (
    <>
      {!selectedMap.is_processing && (
        <Source key={tilesUrl} id="robot-map-source" type="raster" tiles={[tilesUrl]} tileSize={256} scheme="tms">
          <Layer
            id="robot-map-layer"
            source="robot-map-source"
            type="raster"
            paint={
              {
                'raster-opacity': 1.0,
                'raster-fade-duration': 0,
                'raster-color': [
                  'interpolate',
                  ['linear'],
                  ['raster-value'],
                  0,
                  theme === 'dark' ? '#00ff00' : '#ff00c8',
                  255,
                  'rgba(255, 255, 255, 0)',
                ],
                'raster-color-range': [0, 255],
              } as any
            }
          />
        </Source>
      )}
      {erasedPolygons.length > 0 && (
        <Source id="erased-polygons-source" type="geojson" data={erasedGeoJson}>
          <Layer
            id="erased-polygons-layer"
            source="erased-polygons-source"
            type="fill"
            paint={{
              'fill-color': theme === 'dark' ? '#29313a' : '#f0f0f0',
              'fill-opacity': 1.0,
            }}
          />
        </Source>
      )}
    </>
  )
}

export default RobotMapOnMap
